/* eslint-disable react/no-multi-comp */
import { useEffect } from 'react';
import { notifications } from '@mantine/notifications';
import { ErrorBoundary } from 'react-error-boundary';
import { useVibeBuilderRemote } from '@/hooks/useVibeBuilderRemote';
import { useVibeModeDockAvailability } from '@/modules/VibeMode/hooks/useVibeModeDockAvailability';
import { useVibeModeDockLayoutOffset } from '@/modules/VibeMode/hooks/useVibeModeDockLayoutOffset';
import { VibeBuilderRenderMode } from '@/modules/VibeMode/remote-entry.contract';

const ERROR_NOTIFICATION_ID = 'vibe-mode-dock-error';
const ERROR_NOTIFICATION_DURATION = 10_000;

// eslint-disable-next-line unicorn/no-null
const renderNothing = () => null;

const showDockError = (message: string) => {
    notifications.show({
        id: ERROR_NOTIFICATION_ID,
        color: 'red',
        title: 'Vibe Mode is unavailable',
        message,
        autoClose: ERROR_NOTIFICATION_DURATION
    });
};

const logDockError = (error: unknown) => {
    console.error('Vibe mode dock crashed', error);
    showDockError(
        error instanceof Error ? error.message : 'Unexpected dock failure'
    );
};

type VibeModeDockRemoteProps = {
    clientSecret: string;
};

const VibeModeDockRemote = ({ clientSecret }: VibeModeDockRemoteProps) => {
    const { Component, persona, error } = useVibeBuilderRemote();

    useEffect(() => {
        if (!error) {
            return;
        }

        showDockError(error.message);
    }, [error]);

    if (!Component || !persona) {
        // eslint-disable-next-line unicorn/no-null
        return null;
    }

    return (
        <Component
            clientSecret={clientSecret}
            persona={persona}
            renderMode={VibeBuilderRenderMode.Dock}
        />
    );
};

const VibeModeDock = () => {
    const { dockClientSecret } = useVibeModeDockAvailability();

    useVibeModeDockLayoutOffset();

    if (!dockClientSecret) {
        // eslint-disable-next-line unicorn/no-null
        return null;
    }

    return (
        <ErrorBoundary fallbackRender={renderNothing} onError={logDockError}>
            <VibeModeDockRemote clientSecret={dockClientSecret} />
        </ErrorBoundary>
    );
};

export default VibeModeDock;
